import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as tasksService from '../services/tasks.service';
import type { TaskDTO } from '../types/tasks.types';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useTaskList() {
  const [query] = useSearchParams();
  const source = query.get('source') || '';
  const agentId = query.get('agent_id') || '';
  useEffect(() => { setPage(1); }, [source, agentId]);
  const [items, setItems] = useState<TaskDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(50);
  const [status, setStatusRaw] = useState('');
  const [marketId, setMarketIdRaw] = useState('');
  const [category, setCategoryRaw] = useState('');
  const [taskType, setTaskTypeRaw] = useState('');
  const [platform, setPlatformRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (p: number, s: string, m: string, c: string, tt: string, pl: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await tasksService.listTasks({ page: p, pageSize, source, agentId, status: s, marketId: m, category: c, taskType: tt, platform: pl });
      setItems(result.items);
      setTotal(result.total);
      setPage(result.page);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [pageSize, source, agentId]);

  useEffect(() => {
    load(page, status, marketId, category, taskType, platform);
  }, [load, page, status, marketId, category, taskType, platform]);

  const setStatus = useCallback((s: string) => {
    setStatusRaw(s);
    setPage(1);
  }, []);

  const setCategory = useCallback((c: string) => {
    setCategoryRaw(c);
    setPage(1);
  }, []);

  const setMarketId = useCallback((m: string) => {
    setMarketIdRaw(m);
    setPage(1);
  }, []);

  const setTaskType = useCallback((t: string) => {
    setTaskTypeRaw(t);
    setPage(1);
  }, []);

  const setPlatform = useCallback((p: string) => {
    setPlatformRaw(p);
    setPage(1);
  }, []);

  const setPageSize = useCallback((value: number) => {
    setPageSizeRaw(value);
    setPage(1);
  }, []);

  const reload = useCallback(() => {
    load(page, status, marketId, category, taskType, platform);
  }, [load, page, status, marketId, category, taskType, platform]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items, total, page, pageSize, totalPages,
    status, marketId, category, taskType, platform,
    loading, error,
    setPage, setPageSize, setStatus, setMarketId, setCategory, setTaskType, setPlatform, reload,
  };
}
